/**
 * canvas-engine/renderer/elements/ImageElement.jsx
 *
 * OWN per-type element renderer for `image` elements.
 *
 * ZERO REUSE: imports NOTHING from template-builder/ or academy-ui/src/template/.
 *
 * Inner-content vs positioning CONTRACT: renders ONLY the inner <img>, filling
 * its positioned box. The parent CanvasRenderer owns absolute positioning.
 * DOM output is identical in builder and reader modes. (Req 4.1, 14.1)
 *
 * Resolves the display URL through the OWN `assetRef` from `content.asset`
 * (falling back to `content.src`). Pure / presentational. (Req 22.4)
 */

import React from 'react';
import { assetRef } from '../../model/assetRef';
import { EngineImage } from '../../model/assetUrlResolver';

function str(v) {
  return v == null ? '' : String(v);
}

function num(v, fallback) {
  return Number.isFinite(v) ? v : fallback;
}

/**
 * Render a single image from the element's asset reference.
 *
 * @param {object} props
 * @param {object} props.element normalized `image` Canvas_Element
 */
export default function ImageElement({ element }) {
  const content = (element && element.content) || {};
  const style = (element && element.style) || {};

  // Prefer the single asset reference; fall back to a plain `src` string.
  const asset = assetRef(content.asset != null ? content.asset : content.src);
  const src = asset.displayLink || asset.storedLink || '';
  const alt = str(content.alt);

  const rootStyle = {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
    borderRadius: num(style.radius, 16),
    boxShadow: typeof style.shadow === 'string' && style.shadow !== 'none' ? style.shadow : 'none',
  };

  const imgStyle = {
    width: '100%',
    height: '100%',
    objectFit: typeof style.objectFit === 'string' ? style.objectFit : 'cover',
    objectPosition: typeof style.objectPosition === 'string' ? style.objectPosition : 'center',
    opacity: Number.isFinite(style.opacity) ? style.opacity : 1,
    display: 'block',
  };

  return (
    <div className="ce-image" style={rootStyle}>
      {src ? (
        <EngineImage className="ce-image__img" src={src} alt={alt} style={imgStyle} />
      ) : (
        <div
          className="ce-image__placeholder"
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#e2e8f0',
            color: '#64748b',
            fontSize: 13,
          }}
          aria-label={alt || 'image placeholder'}
        >
          {alt || 'Image'}
        </div>
      )}
    </div>
  );
}
